import { motion } from 'framer-motion';
import { ShieldCheck, Leaf, Award, Droplets } from 'lucide-react';
import { TESTIDS } from '@/constants/testIds';

const SPICE_MACRO = 'https://images.pexels.com/photos/3040873/pexels-photo-3040873.jpeg';
const MAPRIX_LOGO = '/images/maprix-logo.jpg';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, delay },
});

const timelineItems = [
  { icon: '🌶️', title: 'Guntur Chillies', desc: 'Bold & vibrant', color: '#C0392B' },
  { icon: '🟡', title: 'Sangli & Hingoli Turmeric', desc: 'Golden & pure', color: '#E8A020' },
  { icon: '🌿', title: 'Indore Coriander', desc: 'Fresh & earthy', color: '#5C8A3D' },
  { icon: '🫙', title: 'Kerala Whole Spices', desc: 'Authentic & aromatic', color: '#5C3D11' },
];

const processSteps = [
  { step: '01', title: 'Careful Selection', desc: 'Raw spices picked from heartland farms across India' },
  { step: '02', title: 'Thorough Cleaning', desc: 'Every batch meticulously cleaned for purity' },
  { step: '03', title: 'Slow Grinding', desc: 'Ground slowly to lock in aroma, colour, and flavour' },
  { step: '04', title: 'Hygienic Sealing', desc: 'Packed in resealable pouches for lasting freshness' },
];

export default function AboutPage() {
  return (
    <div data-testid={TESTIDS.ABOUT_PAGE}>
      {/* ===== PAGE HERO ===== */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 grid grid-cols-1 md:grid-cols-2">
          <div className="bg-[#1B3A6B]" />
          <div className="hidden md:block relative">
            <img src={SPICE_MACRO} alt="Spices" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
            <div className="absolute inset-0 bg-[#1B3A6B]/30" />
          </div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40 w-full">
          <motion.div {...fadeUp()}>
            <p className="font-cinzel text-xs tracking-[0.25em] uppercase text-[#C9922C] mb-4">About Us</p>
            <h1 className="font-playfair text-[#F9F3E8] text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4">
              The Story Behind<br />the Spice
            </h1>
            <p className="font-hindi text-[#C9922C] text-xl">अब प्यार के साथ स्वाद भी!!</p>
          </motion.div>
        </div>
      </section>

      {/* ===== FOUNDER'S VISION ===== */}
      <section className="bg-[#F9F3E8] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.blockquote {...fadeUp()} className="text-center mb-16">
            <p className="font-playfair text-[#1B3A6B] text-2xl md:text-4xl italic leading-relaxed max-w-4xl mx-auto">
              "Rooted in India. Ground with Love."
            </p>
            <div className="w-16 h-0.5 bg-[#C9922C] mx-auto mt-6" />
          </motion.blockquote>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
            <motion.div {...fadeUp(0.1)}>
              <p className="font-lato text-[#5C3D11] text-base md:text-lg leading-relaxed mb-6">
                At Anuruchi Masale, we bring you a delightful range of premium-quality spices that add rich flavors and aromas to your culinary creations. Our spices are carefully selected and hygienically packed to ensure freshness and authenticity in every pinch.
              </p>
              <p className="font-lato text-[#5C3D11] text-base md:text-lg leading-relaxed">
                The visionary behind Anuruchi Masale, Rushikesh, started this journey with a passion for delivering authentic spices. Having worked with exporters and domestic clients in wholesale trades, he now envisions capturing the retail B2B market, bringing high-quality spices directly to professional kitchens across India.
              </p>
            </motion.div>

            <motion.div {...fadeUp(0.2)} className="flex flex-col items-center gap-6 bg-white p-8 rounded-lg shadow-lg border border-[#C9922C]/15">
              <img src={MAPRIX_LOGO} alt="Maprix Industries" className="h-20 rounded" />
              <div className="text-center">
                <h3 className="font-playfair text-[#1B3A6B] text-xl mb-2">Maprix Industries Pvt. Ltd.</h3>
                <p className="font-cinzel text-xs tracking-wider uppercase text-[#C9922C]">Manufacturer & Distributor</p>
                <p className="font-lato text-[#5C3D11]/70 text-sm mt-3">MIDC Jalgaon, Maharashtra</p>
                <p className="font-lato text-[#5C3D11]/70 text-sm">FSSAI Lic. No. 21524031001278</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== SOURCING TIMELINE ===== */}
      <section className="bg-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp()} className="text-center mb-16">
            <p className="font-cinzel text-xs tracking-[0.25em] uppercase text-[#C9922C] mb-3">Sourcing</p>
            <h2 className="font-playfair text-[#1B3A6B] text-3xl md:text-5xl">From India's Heartland</h2>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-[#C9922C]/30 -translate-y-1/2" />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {timelineItems.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                  className="text-center relative"
                >
                  <div
                    className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl border-2 bg-[#F9F3E8] relative z-10"
                    style={{ borderColor: item.color }}
                  >
                    <span>{item.icon}</span>
                  </div>
                  <h4 className="font-playfair text-[#1B3A6B] text-lg mb-1">{item.title}</h4>
                  <p className="font-lato text-[#5C3D11]/70 text-sm">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== QUALITY PROCESS ===== */}
      <section className="bg-[#F9F3E8] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp()} className="text-center mb-16">
            <p className="font-cinzel text-xs tracking-[0.25em] uppercase text-[#C9922C] mb-3">Our Process</p>
            <h2 className="font-playfair text-[#1B3A6B] text-3xl md:text-5xl">Quality at Every Step</h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="bg-white p-8 border border-[#C9922C]/15 hover:border-[#C9922C]/50 transition-all duration-500 relative group"
              >
                <span className="font-cinzel text-[#C9922C]/20 text-5xl font-bold absolute top-4 right-4 group-hover:text-[#C9922C]/40 transition-colors">
                  {step.step}
                </span>
                <div className="relative z-10">
                  <div className="w-1 h-8 bg-[#C9922C] mb-4" />
                  <h4 className="font-playfair text-[#1B3A6B] text-xl mb-2">{step.title}</h4>
                  <p className="font-lato text-[#5C3D11] text-sm leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CERTIFICATIONS ===== */}
      <section className="bg-[#1B3A6B] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp()} className="text-center mb-12">
            <h2 className="font-playfair text-[#F9F3E8] text-3xl md:text-4xl">Certifications & Compliance</h2>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: ShieldCheck, label: 'FSSAI Certified' },
              { icon: Award, label: 'Made in India' },
              { icon: Leaf, label: '100% Natural' },
              { icon: Droplets, label: 'No Preservatives' },
            ].map((cert, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="flex flex-col items-center gap-3 bg-[#1B3A6B] border border-[#C9922C]/25 p-6 rounded-lg"
              >
                <cert.icon size={36} className="text-[#C9922C]" />
                <span className="font-cinzel text-[#F9F3E8] text-xs tracking-wider uppercase text-center">{cert.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
